import type { FC } from 'react';
import {
  Activity,
  AlertTriangle,
  Ban,
  BadgeCheck,
  BadgeX,
  BellRing,
  CheckCircle,
  Clock,
  CreditCard,
  Euro,
  Eye,
  FileText,
  Flag,
  Hourglass,
  KeyRound,
  Landmark,
  Link,
  Lock,
  Mail,
  MessageSquare,
  Paperclip,
  Receipt,
  RotateCcw,
  Scale,
  Send,
  Settings,
  Shield,
  Star,
  Unlock,
  UserCog,
  Users,
  Wrench,
  XCircle,
} from 'lucide-react';
import { SavV2Event, SavV2EventType } from '@/models/savV2Event';

/**
 * Affichage des événements de l'historique SAV (outil-pro).
 *
 * L'API décide de ce que voit chaque audience (cf. api
 * `sav-v2/constants/sav-event-visibility.const.ts`) : le vendeur voit tout sauf
 * le chat interne et ses pièces jointes. Le front affiche tout ce qu'il reçoit ;
 * un type encore inconnu du front s'affiche avec la config par défaut.
 */

export interface TimelineEventConfig {
  icon: FC<{ className?: string }>;
  color: string;
  label: string;
}

const GRAY = 'tw-text-gray-600 tw-bg-gray-100';
const BLUE = 'tw-text-blue-600 tw-bg-blue-100';
const GREEN = 'tw-text-green-600 tw-bg-green-100';
const RED = 'tw-text-red-600 tw-bg-red-100';
const AMBER = 'tw-text-amber-600 tw-bg-amber-100';
const PURPLE = 'tw-text-purple-600 tw-bg-purple-100';
const INDIGO = 'tw-text-indigo-600 tw-bg-indigo-100';
const CYAN = 'tw-text-cyan-600 tw-bg-cyan-100';
const ORANGE = 'tw-text-orange-600 tw-bg-orange-100';
const TEAL = 'tw-text-teal-600 tw-bg-teal-100';

/** Exhaustif : TypeScript exige un libellé pour chaque type d'événement. */
const TIMELINE_EVENT_CONFIG: Record<SavV2EventType, TimelineEventConfig> = {
  [SavV2EventType.DECLARED]: { icon: FileText, color: BLUE, label: 'Panne déclarée' },
  [SavV2EventType.REPAIRER_ASSIGNED]: { icon: Wrench, color: ORANGE, label: 'Réparateur assigné' },
  [SavV2EventType.REPAIRER_UNASSIGNED]: { icon: Wrench, color: GRAY, label: 'Réparateur désassigné' },
  [SavV2EventType.REPAIRER_CONTACTED]: { icon: Wrench, color: PURPLE, label: 'Réparateur contacté' },
  [SavV2EventType.REPAIRER_CREATED_FROM_GOOGLE_PLACE]: { icon: Wrench, color: CYAN, label: 'Réparateur créé' },
  [SavV2EventType.QUOTE_UPLOADED]: { icon: FileText, color: GREEN, label: 'Devis uploadé' },
  [SavV2EventType.QUOTE_SELECTED_FOR_ANALYSIS]: { icon: Eye, color: INDIGO, label: 'Devis sélectionné' },
  [SavV2EventType.ANALYSIS_COMPLETED]: { icon: CheckCircle, color: TEAL, label: 'Analyse terminée' },
  [SavV2EventType.QUOTE_INVALIDATED]: { icon: XCircle, color: RED, label: 'Devis invalidé' },
  [SavV2EventType.INTERVENTION_CREATED]: { icon: Settings, color: BLUE, label: 'Intervention créée' },
  [SavV2EventType.INTERVENTION_CLOSED]: { icon: CheckCircle, color: GREEN, label: 'Intervention clôturée' },
  [SavV2EventType.INTERVENTION_REOPENED]: { icon: RotateCcw, color: AMBER, label: 'Intervention rouverte' },
  [SavV2EventType.INTERVENTION_FULLY_COMPLETED]: { icon: CheckCircle, color: GREEN, label: 'Intervention soldée' },
  [SavV2EventType.DECISION_CREATED]: { icon: Euro, color: BLUE, label: 'Décision créée' },
  [SavV2EventType.DECISION_UPDATED]: { icon: Euro, color: AMBER, label: 'Décision mise à jour' },
  [SavV2EventType.DECISION_DELETED]: { icon: Euro, color: RED, label: 'Décision supprimée' },
  [SavV2EventType.DECISION_SENT_TO_GARANT]: { icon: Send, color: PURPLE, label: 'Envoyée au garant' },
  [SavV2EventType.DECISION_REMINDER_SENT]: { icon: BellRing, color: AMBER, label: 'Relance envoyée' },
  [SavV2EventType.GARANT_VALIDATION_LINK_RENEWED]: { icon: Link, color: GRAY, label: 'Lien de validation renouvelé' },
  [SavV2EventType.DECISION_VALIDATED]: { icon: BadgeCheck, color: GREEN, label: 'Décision validée' },
  [SavV2EventType.DECISION_REFUSED]: { icon: BadgeX, color: RED, label: 'Décision refusée' },
  [SavV2EventType.DECISION_CANCELLED]: { icon: Ban, color: GRAY, label: 'Décision annulée' },
  [SavV2EventType.DECISION_SIGNED_BY_MANDATE]: { icon: BadgeCheck, color: GREEN, label: 'Décision signée par mandat' },
  [SavV2EventType.DECISION_VALIDATION_RESET]: { icon: RotateCcw, color: AMBER, label: 'Validation réinitialisée' },
  [SavV2EventType.COVERAGE_DECISION_CREATED]: { icon: Euro, color: TEAL, label: 'Prise en charge définie' },
  [SavV2EventType.SIGNATURE_CANCELLED]: { icon: Ban, color: GRAY, label: 'Signature annulée' },
  [SavV2EventType.MESSAGE_INTERNAL]: { icon: Users, color: PURPLE, label: 'Message interne' },
  [SavV2EventType.MESSAGE_TO_BENEFICIARY]: { icon: Mail, color: GREEN, label: 'Message au bénéficiaire' },
  [SavV2EventType.MESSAGE_FROM_BENEFICIARY]: { icon: MessageSquare, color: BLUE, label: 'Message du bénéficiaire' },
  [SavV2EventType.MESSAGE_TO_GARANT]: { icon: Shield, color: INDIGO, label: 'Message au garant' },
  [SavV2EventType.MESSAGE_FROM_GARANT]: { icon: Shield, color: CYAN, label: 'Message du garant' },
  [SavV2EventType.MESSAGE_BENEF_PROCESSED]: { icon: CheckCircle, color: GRAY, label: 'Messages bénéficiaire traités' },
  [SavV2EventType.MESSAGE_GARANT_PROCESSED]: { icon: CheckCircle, color: GRAY, label: 'Messages garant traités' },
  [SavV2EventType.MESSAGE_INTERNAL_PROCESSED]: { icon: CheckCircle, color: GRAY, label: 'Messages internes traités' },
  [SavV2EventType.DOCUMENT_ADDED]: { icon: Paperclip, color: GRAY, label: 'Document ajouté' },
  [SavV2EventType.DOCUMENT_ADDED_INTERNAL]: { icon: Paperclip, color: GRAY, label: 'Document non partagé ajouté' },
  [SavV2EventType.COMPLETED]: { icon: CheckCircle, color: GREEN, label: 'SAV terminé' },
  [SavV2EventType.CANCELLED]: { icon: XCircle, color: RED, label: 'SAV annulé' },
  [SavV2EventType.SANS_SUITE]: { icon: Ban, color: GRAY, label: 'Classé sans suite' },
  [SavV2EventType.PENDING_ACTION_SET]: { icon: Hourglass, color: AMBER, label: 'Action en attente' },
  [SavV2EventType.PENDING_ACTION_COMPLETED]: { icon: CheckCircle, color: GREEN, label: 'Action en attente réalisée' },
  [SavV2EventType.PENDING_ACTION_CANCELLED]: { icon: XCircle, color: GRAY, label: 'Action en attente annulée' },
  [SavV2EventType.LEGAL_BLOCKED]: { icon: Scale, color: RED, label: 'Blocage juridique' },
  [SavV2EventType.LEGAL_UNBLOCKED]: { icon: Unlock, color: GREEN, label: 'Déblocage juridique' },
  [SavV2EventType.MANAGER_ASSIGNED]: { icon: UserCog, color: BLUE, label: 'Gestionnaire assigné' },
  [SavV2EventType.MANAGER_CHANGED]: { icon: UserCog, color: AMBER, label: 'Gestionnaire changé' },
  [SavV2EventType.SATISFACTION_SURVEY_SENT]: { icon: Star, color: GRAY, label: 'Enquête de satisfaction envoyée' },
  [SavV2EventType.SATISFACTION_SURVEY_RATED]: { icon: Star, color: AMBER, label: 'Note de satisfaction' },
  [SavV2EventType.SATISFACTION_SURVEY_SUBMITTED]: { icon: Star, color: GREEN, label: 'Enquête de satisfaction complétée' },
  [SavV2EventType.BENEFICIARY_EMAIL_UPDATED]: { icon: Mail, color: GRAY, label: 'Email du bénéficiaire modifié' },
  [SavV2EventType.AWAITING_FLAGS_UPDATED]: { icon: Clock, color: GRAY, label: 'Attentes mises à jour' },
  [SavV2EventType.REPAIRER_INVOICE_CREATED]: { icon: Receipt, color: BLUE, label: 'Facture réparateur créée' },
  [SavV2EventType.REPAIRER_INVOICE_ARMED]: { icon: Receipt, color: BLUE, label: 'Facture réparateur attendue' },
  [SavV2EventType.REPAIRER_INVOICE_UPLOADED]: { icon: Receipt, color: GREEN, label: 'Facture réparateur déposée' },
  [SavV2EventType.REPAIRER_INVOICE_DISPUTED]: { icon: AlertTriangle, color: RED, label: 'Facture réparateur contestée' },
  [SavV2EventType.REPAIRER_INVOICE_VALIDATED]: { icon: BadgeCheck, color: GREEN, label: 'Facture réparateur validée' },
  [SavV2EventType.REPAIRER_INVOICE_SENT_TO_GARANT]: { icon: Send, color: PURPLE, label: 'Facture réparateur transmise' },
  [SavV2EventType.REPAIRER_INVOICE_CANCELLED]: { icon: Ban, color: GRAY, label: 'Facture réparateur annulée' },
  [SavV2EventType.GARANT_DECLARED_PAID]: { icon: CreditCard, color: GREEN, label: 'Paiement déclaré' },
  [SavV2EventType.REPAIRER_CONFIRMED_PAYMENT]: { icon: BadgeCheck, color: GREEN, label: 'Paiement confirmé par le réparateur' },
  [SavV2EventType.REPAIRER_CONFIRMED_PAYMENT_AFTER_DISPUTE]: { icon: BadgeCheck, color: GREEN, label: 'Paiement confirmé après litige' },
  [SavV2EventType.REPAIRER_DISPUTED_PAYMENT]: { icon: AlertTriangle, color: RED, label: 'Paiement contesté par le réparateur' },
  [SavV2EventType.PAYMENT_ESCALATED_TO_AFFINICAR]: { icon: Flag, color: RED, label: 'Paiement remonté à Affinicar' },
  [SavV2EventType.COMPANY_RIB_UPDATED_FROM_INVOICE]: { icon: Landmark, color: GRAY, label: 'RIB réparateur mis à jour' },
  [SavV2EventType.REPAIRER_INVOICE_OTP_SENT]: { icon: KeyRound, color: GRAY, label: 'Code envoyé au réparateur' },
  [SavV2EventType.REPAIRER_INVOICE_OTP_VALIDATED]: { icon: Lock, color: GRAY, label: 'Réparateur authentifié' },
};

const DEFAULT_TIMELINE_EVENT_CONFIG: TimelineEventConfig = { icon: Activity, color: GRAY, label: 'Événement' };

export function getTimelineEventConfig(type: string): TimelineEventConfig {
  return TIMELINE_EVENT_CONFIG[type as SavV2EventType] || DEFAULT_TIMELINE_EVENT_CONFIG;
}

const REPAIRER_CONTACT_OUTCOMES: Record<string, string> = {
  available: 'Disponible',
  unavailable: 'Non disponible',
  no_answer: 'Pas de réponse',
  pending: 'En attente',
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Valeur d'acteur affichable : un UUID technique ou 'system' n'apprend rien au lecteur. */
function readable(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value);
  if (UUID_PATTERN.test(text) || text === 'system') return undefined;
  return text;
}

function formatCents(cents: unknown): string | undefined {
  const value = typeof cents === 'string' ? Number(cents) : cents;
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return `${(value / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function formatDateTime(date: unknown): string | undefined {
  if (!date) return undefined;
  const parsed = new Date(String(date));
  if (Number.isNaN(parsed.getTime())) return readable(date);
  return parsed.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDay(date: unknown): string | undefined {
  if (!date) return undefined;
  const parsed = new Date(String(date));
  if (Number.isNaN(parsed.getTime())) return readable(date);
  return parsed.toLocaleDateString('fr-FR');
}

/** Résumé d'une ligne sous le libellé de l'événement. */
export function getTimelineDescription(event: SavV2Event): string {
  const m = event.metadata;
  if (!m) return '';
  switch (event.eventType) {
    case SavV2EventType.REPAIRER_ASSIGNED:
      return m.repairerName ? `Assigné à ${m.repairerName}` : '';
    case SavV2EventType.REPAIRER_UNASSIGNED:
      return m.previousRepairerName || m.repairerName || '';
    case SavV2EventType.REPAIRER_CONTACTED:
      return m.outcome ? REPAIRER_CONTACT_OUTCOMES[m.outcome] || m.outcome : '';
    case SavV2EventType.QUOTE_UPLOADED:
    case SavV2EventType.QUOTE_SELECTED_FOR_ANALYSIS:
    case SavV2EventType.QUOTE_INVALIDATED:
    case SavV2EventType.DOCUMENT_ADDED:
    case SavV2EventType.DOCUMENT_ADDED_INTERNAL:
      return m.fileName || '';
    case SavV2EventType.DECISION_SENT_TO_GARANT:
      return m.garantName ? `Envoyé à ${m.garantName}` : '';
    case SavV2EventType.DECISION_VALIDATED: {
      const by = readable(m.validatedBy);
      return by ? `Par ${by}` : '';
    }
    case SavV2EventType.DECISION_REFUSED:
      return m.refusalReason || m.reason || '';
    case SavV2EventType.CANCELLED:
      return m.cancelReason || m.reason || '';
    case SavV2EventType.MESSAGE_TO_BENEFICIARY:
    case SavV2EventType.MESSAGE_FROM_BENEFICIARY:
    case SavV2EventType.MESSAGE_TO_GARANT:
    case SavV2EventType.MESSAGE_FROM_GARANT:
    case SavV2EventType.MESSAGE_INTERNAL:
      return m.subject ? `Objet : ${m.subject}` : '';
    case SavV2EventType.MANAGER_ASSIGNED:
    case SavV2EventType.MANAGER_CHANGED:
      return m.managerName || '';
    case SavV2EventType.COVERAGE_DECISION_CREATED: {
      const covered = formatCents(m.totalCovered ?? m.coveredAmount);
      return covered ? `Pris en charge : ${covered}` : '';
    }
    case SavV2EventType.REPAIRER_INVOICE_UPLOADED:
    case SavV2EventType.REPAIRER_INVOICE_SENT_TO_GARANT:
      return formatCents(m.amountTtcCents) ? `${formatCents(m.amountTtcCents)} TTC` : '';
    case SavV2EventType.GARANT_DECLARED_PAID:
      return m.paymentReference ? `Réf. ${m.paymentReference}` : '';
    case SavV2EventType.SATISFACTION_SURVEY_RATED:
    case SavV2EventType.SATISFACTION_SURVEY_SUBMITTED:
      return m.rating != null ? `Note : ${m.rating}/5` : '';
    default:
      return m.reason || m.description || '';
  }
}

type DetailRow = { label: string; value: string };

/** Lignes du détail déplié d'un événement. */
export function getEventDetailRows(event: SavV2Event): DetailRow[] {
  const m = event.metadata;
  const rows: DetailRow[] = [];
  const add = (label: string, value: unknown) => {
    if (value == null || value === '') return;
    rows.push({ label, value: String(value) });
  };

  // Date/heure complète + auteur (toujours affichés)
  add('Date', formatDateTime(event.createdAt));
  add('Auteur', event.createdByName);
  if (!m) return rows;

  switch (event.eventType) {
    case SavV2EventType.REPAIRER_ASSIGNED:
      add('Réparateur', m.repairerName);
      if (m.isGarantWorkflow) add('Prise en charge', 'Directe par le garant');
      break;
    case SavV2EventType.REPAIRER_UNASSIGNED:
      add('Réparateur', m.previousRepairerName || m.repairerName);
      add('Raison', m.reason);
      break;
    case SavV2EventType.REPAIRER_CONTACTED:
      add('Résultat', m.outcome ? REPAIRER_CONTACT_OUTCOMES[m.outcome] || m.outcome : undefined);
      add('Réparateur', m.repairerName);
      add('Moyen de contact', m.contactMethod);
      add('Notes', m.notes);
      break;
    case SavV2EventType.REPAIRER_CREATED_FROM_GOOGLE_PLACE:
      add('Réparateur', m.repairerName);
      break;
    case SavV2EventType.QUOTE_UPLOADED:
    case SavV2EventType.QUOTE_SELECTED_FOR_ANALYSIS:
      add('Fichier', m.fileName);
      add('Taille', m.fileSize ? `${Math.round(m.fileSize / 1024)} KB` : undefined);
      add('Réparateur', m.repairerName);
      if (m.negotiated) add('Type', 'Devis négocié');
      if (m.competing) add('Type', 'Devis concurrent');
      break;
    case SavV2EventType.QUOTE_INVALIDATED:
      add('Fichier', m.fileName);
      add('Raison', m.reason);
      break;
    case SavV2EventType.INTERVENTION_CLOSED:
    case SavV2EventType.INTERVENTION_REOPENED:
      add('Intervention', m.interventionNumber);
      add('Issue', m.interventionOutcome);
      add('Raison', m.reason);
      if (m.previousAmounts && typeof m.previousAmounts === 'object') {
        add('Montant bénéficiaire précédent', formatCents(m.previousAmounts.beneficiary));
        add('Montant pris en charge précédent', formatCents(m.previousAmounts.covered));
        add('Geste commercial précédent', formatCents(m.previousAmounts.commercial));
      }
      break;
    case SavV2EventType.DECISION_CREATED:
    case SavV2EventType.DECISION_UPDATED: {
      const types: Record<string, string> = { ACCORD: 'Accord total', PARTIAL: 'Accord partiel', REFUSAL: 'Refus' };
      add('Type', m.decisionType ? types[m.decisionType] || m.decisionType : undefined);
      add('Montant', typeof m.amount === 'number' ? formatCents(m.amount) : m.amount);
      add('Modifiée par', readable(m.updatedBy));
      break;
    }
    case SavV2EventType.DECISION_DELETED:
      add('Supprimée par', readable(m.deletedBy));
      break;
    case SavV2EventType.DECISION_SENT_TO_GARANT:
      add('Garant', m.garantName);
      add('Canal', m.channel);
      if (m.byMandate) add('Mode', 'Signature par mandat');
      break;
    case SavV2EventType.DECISION_REMINDER_SENT:
      add('Destinataire', m.recipient);
      add('Canal', m.channel);
      break;
    case SavV2EventType.DECISION_VALIDATED:
      add('Validé par', readable(m.validatedBy));
      add('Mode de validation', m.validationMethod);
      break;
    case SavV2EventType.DECISION_REFUSED:
      add('Raison du refus', m.refusalReason || m.reason);
      add('Refusé par', readable(m.refusedBy));
      break;
    case SavV2EventType.DECISION_VALIDATION_RESET:
      add('Statut précédent', m.resetFrom);
      break;
    case SavV2EventType.COVERAGE_DECISION_CREATED:
      add('Montant bénéficiaire', formatCents(m.beneficiaryAmount));
      add('Montant pris en charge', formatCents(m.coveredAmount));
      add('Geste commercial', formatCents(m.commercialAmount));
      add('Total pris en charge', formatCents(m.totalCovered));
      add('Montant total', formatCents(m.totalAmount));
      add('Coût interne pris en charge', formatCents(m.internalCoveredAmount));
      add('Geste commercial interne', formatCents(m.internalCommercialAmount));
      add('Commentaire', m.internalComment);
      break;
    case SavV2EventType.MESSAGE_INTERNAL:
    case SavV2EventType.MESSAGE_TO_BENEFICIARY:
    case SavV2EventType.MESSAGE_FROM_BENEFICIARY:
    case SavV2EventType.MESSAGE_TO_GARANT:
    case SavV2EventType.MESSAGE_FROM_GARANT:
      add('Objet', m.subject);
      add('Aperçu', m.messagePreview);
      if (m.hasAttachments) add('Pièces jointes', 'Oui');
      break;
    case SavV2EventType.DOCUMENT_ADDED:
    case SavV2EventType.DOCUMENT_ADDED_INTERNAL:
      add('Fichier', m.fileName);
      add('Taille', m.fileSize ? `${Math.round(m.fileSize / 1024)} KB` : undefined);
      add('Type', m.documentType);
      break;
    case SavV2EventType.CANCELLED:
    case SavV2EventType.SANS_SUITE:
      add('Raison', m.cancelReason || m.reason);
      break;
    case SavV2EventType.MANAGER_ASSIGNED:
    case SavV2EventType.MANAGER_CHANGED:
      add('Gestionnaire', m.managerName);
      add('Gestionnaire précédent', m.previousManagerName);
      break;
    case SavV2EventType.LEGAL_BLOCKED:
      add('Date du courrier', formatDay(m.letterDate));
      add('Motif', m.reason);
      break;
    case SavV2EventType.LEGAL_UNBLOCKED:
      add('Motif du déblocage', m.reason);
      add('Motif du blocage', m.originalBlockReason);
      break;
    case SavV2EventType.PENDING_ACTION_SET:
      add('Action', m.actionType);
      add('Attendue de', m.actor);
      break;
    case SavV2EventType.PENDING_ACTION_CANCELLED:
      add('Action', m.previousType);
      add('Raison', m.reason);
      break;
    case SavV2EventType.SATISFACTION_SURVEY_RATED:
    case SavV2EventType.SATISFACTION_SURVEY_SUBMITTED:
      add('Note', m.rating != null ? `${m.rating}/5` : undefined);
      add('Commentaire', m.comment);
      break;
    case SavV2EventType.BENEFICIARY_EMAIL_UPDATED:
      add('Ancien email', m.oldEmail);
      add('Nouvel email', m.newEmail);
      break;
    case SavV2EventType.REPAIRER_INVOICE_UPLOADED:
      add('N° de facture', m.invoiceNumber);
      add('Montant TTC', formatCents(m.amountTtcCents));
      add('IBAN', m.ibanMasked);
      break;
    case SavV2EventType.REPAIRER_INVOICE_SENT_TO_GARANT:
      add('Montant TTC', formatCents(m.amountTtcCents));
      add('Titulaire', m.accountHolderSnapshot);
      add('IBAN', m.ibanSnapshot);
      add('BIC', m.bicSnapshot);
      break;
    case SavV2EventType.REPAIRER_INVOICE_DISPUTED:
    case SavV2EventType.REPAIRER_INVOICE_CANCELLED:
      add('Motif', m.reason);
      break;
    case SavV2EventType.GARANT_DECLARED_PAID:
      add('Date du paiement', formatDay(m.paymentDate));
      add('Référence', m.paymentReference);
      break;
    case SavV2EventType.REPAIRER_CONFIRMED_PAYMENT:
    case SavV2EventType.REPAIRER_CONFIRMED_PAYMENT_AFTER_DISPUTE:
      add('Confirmé le', formatDateTime(m.confirmedAt));
      add('Note', m.resolutionNote);
      break;
    case SavV2EventType.REPAIRER_DISPUTED_PAYMENT:
      add('Contesté le', formatDateTime(m.disputedAt));
      break;
    case SavV2EventType.COMPANY_RIB_UPDATED_FROM_INVOICE:
      add('IBAN', m.ibanMasked);
      break;
    case SavV2EventType.REPAIRER_INVOICE_OTP_SENT:
      add('Envoyé à', m.sentToEmail);
      break;
    default:
      add('Description', m.description);
      add('Raison', m.reason);
      break;
  }
  return rows;
}
